import { useEffect, useRef, useState } from "react";

/**
 * Crossfades the form out and the result panel in, instead of swapping them in
 * a single frame.
 *
 * "Result", not "confirmation": the outcome slot holds either the thank-you or
 * the error panel, and the wrapper decides which. This component only knows
 * that one panel is leaving and another is arriving.
 *
 * Sequential, not simultaneous: the two panels are very different heights, so
 * overlapping them would mean animating one on top of the other inside a box
 * that is resizing at the same time. Fading out, swapping, then fading in keeps
 * every frame showing exactly one panel.
 *
 * Hand-rolled rather than a Headless UI `Transition`, because both wrappers
 * live inside a `<Transition>` tree already: HUI 1.7 treats a nested
 * `Transition` as a `Transition.Child` and throws outright on `show` ("You are
 * already inside a <Transition /> component"). Opacity plus two timers is less
 * machinery than working around that.
 */

type Props = {
  /**
   * Which panel to show. Flipping it back rewinds to the form — that is the
   * retry path, so `resultPanel` must keep rendering the panel that is leaving
   * until the rewind completes, or the fade-out shows the wrong outcome.
   */
  showResult: boolean;
  formPanel: React.ReactNode;
  resultPanel: React.ReactNode;
};

/**
 * Must stay >= the `duration-*` on the leaving panel. A shorter value would cut
 * the fade off mid-way and swap on a visible panel; the small excess is one
 * frame of an already-invisible element.
 */
const FADE_OUT_MS = 120;

/** Keep in step with the `duration-200` on the entering panel. */
const FADE_IN_MS = 200;

/**
 * Backstop for releasing the height pin, measured from the last time the pin
 * changed. Must outlast the whole sequence — fade out, two frames, fade in —
 * or it fires while the height is still interpolating and snaps the card open.
 */
const PIN_RELEASE_FALLBACK_MS = FADE_OUT_MS + FADE_IN_MS + 150;

const FeedbackPanelSwap: React.FC<Props> = ({
  showResult,
  formPanel,
  resultPanel,
}) => {
  const [phase, setPhase] = useState<"form" | "leaving" | "result">("form");
  // Has the panel currently on screen finished arriving? Separate from `phase`
  // because entering needs two commits: mount at opacity-0, then flip. Mounting
  // straight to opacity-100 gives no transition to run — the browser has
  // nothing to interpolate from. Reset to false at every panel switch, which
  // includes the rewind to the form: without that the retry path snaps back at
  // full opacity while the forward path fades.
  const [entered, setEntered] = useState(false);

  const boxRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  /**
   * Height the box is pinned to for the duration of the swap, or null to let it
   * size itself again. Without this the box jumps to the confirmation's height
   * the instant the panels swap — while both are invisible — so the only thing
   * the eye catches is an empty card snapping shut. Pinned, the resize instead
   * runs alongside the fade-in.
   */
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);

  // Keyed on `showResult` alone. Adding `phase` here would re-run the effect the
  // moment it changes and the cleanup would clear the very timer that drives
  // the sequence, leaving the form faded out forever.
  useEffect(() => {
    if (!showResult) {
      setPhase("form");
      setEntered(false);
      setLockedHeight(null);
      return;
    }
    // Pin to what the form already measures, before anything moves.
    setLockedHeight(boxRef.current?.getBoundingClientRect().height ?? null);
    setPhase("leaving");
    const timer = setTimeout(() => {
      // Cleared in the same commit the result panel mounts, so it mounts
      // invisible and the effect below has something to fade from.
      setEntered(false);
      setPhase("result");
    }, FADE_OUT_MS);
    return () => clearTimeout(timer);
  }, [showResult]);

  // Drives the fade-in for whichever panel has just mounted. Keyed on `entered`
  // as well as `phase` so the rewind to the form is staged exactly like the
  // arrival of the result: React reuses the DOM node across the ternary, so a
  // panel that mounts already at opacity-100 gives the browser nothing to
  // interpolate and snaps into place.
  useEffect(() => {
    if (phase === "leaving" || entered) {
      return;
    }
    // Two frames: the first lands after React has committed opacity-0 to the
    // DOM, the second guarantees the browser has painted it before the class
    // flips. One frame is usually enough and occasionally isn't.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setEntered(true);
        if (phase !== "result") {
          return;
        }
        // Retarget the pin at the result panel's own height; CSS interpolates
        // between the two, so the card shrinks as the text arrives.
        //
        // Equal heights are the case to watch: retargeting to the value already
        // set starts no transition, so no transitionend fires and the release
        // below never runs — leaving the box pinned to a stale pixel height
        // that a later resize cannot undo. Release straight away instead.
        const height = resultRef.current?.offsetHeight;
        if (height == null) {
          return;
        }
        setLockedHeight((current) => (current === height ? null : height));
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [phase, entered]);

  // The pin is normally released by the height transitionend above. That event
  // is not guaranteed — an interrupted or never-started transition simply never
  // fires one — and a pin that outlives the swap freezes the card at a stale
  // height with the panel's overflow-hidden clipping whatever sits below.
  // Release unconditionally once the animation window has passed.
  useEffect(() => {
    if (lockedHeight === null) {
      return;
    }
    const timer = setTimeout(
      () => setLockedHeight(null),
      PIN_RELEASE_FALLBACK_MS,
    );
    return () => clearTimeout(timer);
  }, [lockedHeight]);

  return (
    // `flex-1` and an explicit height contradict each other — flex-basis 0 wins
    // and the height is ignored — so the pin has to switch the box to
    // `0 0 auto` for as long as it holds.
    <div
      ref={boxRef}
      // `justify-center` matters only while the height is pinned: the panel is
      // centred in the viewport, so it collapses towards its own middle. Centred
      // content therefore holds still as the card closes in around it, where
      // top-aligned content would sit at the top edge and drift.  A no-op for
      // the form, which fills the box via `flex-1`.
      className="flex min-h-0 flex-1 flex-col justify-center"
      style={
        lockedHeight === null
          ? undefined
          : {
              height: lockedHeight,
              flex: "0 0 auto",
              transition: `height ${FADE_IN_MS}ms ease-out`,
            }
      }
      onTransitionEnd={(event) => {
        // Release once the box has arrived, so it sizes to its content again
        // and a later window resize is not fighting a stale pixel value.
        // Opacity transitions from the panels bubble through here too.
        if (
          event.propertyName === "height" &&
          event.target === boxRef.current
        ) {
          setLockedHeight(null);
        }
      }}
    >
      {phase === "result" ? (
        <div
          ref={resultRef}
          className={`transition-opacity duration-200 ease-out ${
            entered ? "opacity-100" : "opacity-0"
          }`}
        >
          {resultPanel}
        </div>
      ) : (
        // The flex classes belong to the form's wrapper, not the result
        // panel's: the form is the shrinkable panel whose textareas
        // flex, and a plain div in that chain would break it.
        <div
          className={`flex min-h-0 flex-1 flex-col transition-opacity duration-100 ease-in ${
            phase === "leaving" || !entered ? "opacity-0" : "opacity-100"
          }`}
        >
          {formPanel}
        </div>
      )}
    </div>
  );
};

export default FeedbackPanelSwap;
